import { motion } from "framer-motion";
import { SectionLabel } from "@/components/Section";

// Brand names with their signature accent colors used ONLY as a small dot.
// Wordmarks are rendered in OneHill's brand palette to keep the design cohesive
// and to avoid trademark / logo reproduction.
const PARTNERS = [
  { name: "Blinkit", accent: "#FFC400" },
  { name: "Instamart", accent: "#FC8019" },
  { name: "Zepto", accent: "#6C2BD9" },
  { name: "BB Now", accent: "#E11D48" },
  { name: "Flipkart Minutes", accent: "#2874F0" },
  { name: "Amazon Now", accent: "#FF9900" },
];

function BrandChip({ name, accent }) {
  return (
    <div className="group flex shrink-0 items-center gap-4 rounded-full border border-neutral-200 bg-white px-7 py-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg hover:border-[#99CC00]/40">
      <span
        className="block h-2.5 w-2.5 rounded-full transition-transform group-hover:scale-125"
        style={{ background: accent, boxShadow: `0 0 0 4px ${accent}22` }}
      />
      <span className="font-display font-black tracking-tight text-xl lg:text-2xl text-[#1F1F1F] whitespace-nowrap">
        {name}
      </span>
    </div>
  );
}

export default function QcPartners() {
  // Duplicate list for seamless infinite scroll
  const looped = [...PARTNERS, ...PARTNERS];
  return (
    <section data-testid="section-qc-partners" className="relative bg-white px-5 py-20 lg:py-24 overflow-hidden">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end mb-12">
          <div>
            <SectionLabel>QC Platforms We Operate Across</SectionLabel>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tight leading-[1.05] text-[#1F1F1F]"
            >
              Built for the platforms shaping <span className="text-[#99CC00]">India’s QC market.</span>
            </motion.h2>
          </div>
          <p className="text-base lg:text-lg leading-relaxed text-neutral-600">
            OneHill supports dark store operations and brand-side marketplace management across India’s leading quick commerce platforms. Brand names shown are for identification only; platform listing and operations are subject to each platform’s own processes.
          </p>
        </div>
      </div>

      {/* Marquee row with edge fades */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-white to-transparent" />
        <div className="flex w-max gap-4 oh-ticker-track" data-testid="qc-partners-marquee">
          {looped.map((p, i) => (
            <BrandChip key={`${p.name}-${i}`} {...p} />
          ))}
        </div>
      </div>

      {/* Static grid for accessibility / SEO crawlers */}
      <div className="mx-auto max-w-7xl mt-10 hidden lg:grid grid-cols-6 gap-3 opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
        {PARTNERS.map((p) => <BrandChip key={p.name} {...p} />)}
      </div>
    </section>
  );
}
